import React, { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import Spinner from "./components/Loaders/Spinner";
import { REACT_APP_URL } from "./Config";
import { useAxiosGet } from "./hooks/useAxios";
import Guide from "./pages/auth/Guide";
import { authSelector, setUserDetails } from "./redux/slice/authSlice";
import { clearConsumerData } from "./redux/slice/consumerSlice";
import { setPromptDisabled } from "./redux/slice/promptSlice";
import { hideLoader, showLoader } from "./redux/slice/siteLoaderSlice";
import { clearToken, tokenSelector } from "./redux/slice/tokenSlice";
import { PRIVATE_NAVIGATION, PUBLIC_NAVIGATION } from "./utils/constants";
const PageNotFound = React.lazy(() => import("./pages/PageNotFound"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const RequiresAuth = React.lazy(() => import("./pages/auth/RequiresAuth"));
const RequiresUnAuth = React.lazy(() => import("./pages/auth/RequiresUnAuth"));
// const BankConsent = React.lazy(
//   () => import("./pages/consumerTrustIndex/BankConsent")
// );
// const BankLogin = React.lazy(
//   () => import("./pages/consumerTrustIndex/BankLogin")
// );
const ConsumerConfiguration = React.lazy(
  () => import("./pages/consumerTrustIndex/Configure")
);
const UpdateProfile = React.lazy(() => import("./pages/profile/UpdateProfile"));
const ConsumerTrustView = React.lazy(
  () => import("./pages/wallet/ConsumerTrustView")
);
const ConsentConfirmationScreen = React.lazy(
  () => import("./pages/consumerTrustIndex/ConsentConfirmation")
);
const ConsentConfirmScreen = React.lazy(
  () => import("./pages/consumerTrustIndex/ConsentConfirm")
);
const ConsumerInitialise = React.lazy(
  () => import("./pages/auth/ConsumerInitialise")
);
const MyCTI = React.lazy(() => import("./pages/auth/MyCTI"));
const VerifySecureCode = React.lazy(
  () => import("./pages/auth/VerifySecureCode")
);
const ConfirmEmail = React.lazy(() => import("./pages/auth/ConfirmEmail"));

export type RouteObjType = {
  path?: string;
  element: JSX.Element;
  children?: RouteObject[];
  errorElement?: JSX.Element;
  feature?: string | boolean;
  permission?: string | boolean;
  organizationType?: string | boolean;
  btnNavigatePath?: string;
  btnTitle?: string;
  sideBarId?: number;
};

const Routes = () => {
  const dispatch = useDispatch();
  const splitData = window.location.pathname.split("/");
  const regex = /^[A-Z]+-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
  const uuidRegex = /^[0-9a-fA-F-]{36}$/;
  const publicRouteData = Object.values(PUBLIC_NAVIGATION);
  let token = useSelector(tokenSelector);
  const userDetails = useSelector(authSelector);
  const [getUserDetails] = useAxiosGet();
  const publicRoutes = [
    {
      path: PUBLIC_NAVIGATION.sandboxConsumerInit,
      element: <Guide />,
      sideBarId: 0,
    },
    {
      path: PUBLIC_NAVIGATION.liveConsumerInit,
      element: <ConsumerInitialise />,
      sideBarId: 0,
    },
    {
      path: PUBLIC_NAVIGATION.viewConsumerCTI,
      element: <MyCTI />,
      sideBarId: 0,
    },
    {
      path: PUBLIC_NAVIGATION.verifySecureCode,
      element: <VerifySecureCode />,
      sideBarId: 0,
    },
    {
      path: PUBLIC_NAVIGATION.confirmEmail,
      element: <ConfirmEmail />,
      sideBarId: 0,
    },
    {
      path: PUBLIC_NAVIGATION.pageNotFound,
      element: <NotFound />,
      sideBarId: 0,
    },
  ];
  const authenticatedRouted: RouteObjType[] = [
    {
      path: PRIVATE_NAVIGATION.profile.edit.path,
      sideBarId: PRIVATE_NAVIGATION.profile.edit.activeSidebar,
      element: <UpdateProfile />,
    },
    {
      path: PRIVATE_NAVIGATION.config.path,
      sideBarId: 0,
      element: <ConsumerConfiguration />,
    },
    // {
    //   path: PRIVATE_NAVIGATION.profile.bankLogin.path,
    //   sideBarId: PRIVATE_NAVIGATION.profile.bankLogin.sideBarId,
    //   element: <BankLogin />,
    // },
    // {
    //   path: PRIVATE_NAVIGATION.profile.bankConsent.path,
    //   sideBarId: PRIVATE_NAVIGATION.profile.bankConsent.sideBarId,
    //   element: <BankConsent />,
    // },
    {
      path: PRIVATE_NAVIGATION.profile.consumerView.path,
      sideBarId: PRIVATE_NAVIGATION.profile.consumerView.activeSidebar,
      element: <ConsumerTrustView />,
    },
    {
      path: PRIVATE_NAVIGATION.profile.consentConfirm.path,
      sideBarId: PRIVATE_NAVIGATION.profile.consentConfirm.activeSidebar,
      element: <ConsentConfirmationScreen />,
    },
    {
      path: PRIVATE_NAVIGATION.profile.consentConfirmScreen.path,
      sideBarId: PRIVATE_NAVIGATION.profile.consentConfirmScreen.activeSidebar,
      element: <ConsentConfirmScreen />,
    },
    {
      path: "*",
      sideBarId: 0,
      element: <PageNotFound />,
    },
  ];

  useEffect(() => {
    if (token) {
      dispatch(showLoader({ header: "", description: "" }));
      getUserData();
    }
  }, [token]);

  const getUserData = async () => {
    const response = await getUserDetails("/consumers/login_consumer_data", {});
    if (response?.data) {
      dispatch(setUserDetails({ userDetails: response?.data }));
    }
    dispatch(hideLoader());
  };

  const IsInitialisedURL = (data: string) => {
    if (!data) return false;

    const dataSplit = data.split("/");

    if (
      splitData[1] &&
      regex.test(splitData[1]) &&
      dataSplit[1] === ":projectId" &&
      splitData[2] &&
      ((uuidRegex.test(splitData[2]) && dataSplit[2] === ":projectApiKey") ||
        (splitData[2] === "sandbox" &&
          splitData[3] &&
          uuidRegex.test(splitData[3]) &&
          dataSplit[3] === ":projectApiKey"))
    ) {
      return true;
    }
    return false;
  };

  const checkURLValidation = () => {
    let index = -1;

    for (let i = 0; i < publicRouteData.length; i++) {
      const val = publicRouteData[i];
      const dataSplit = val.split("/");

      if (IsInitialisedURL(val)) index = i;
      else if (
        splitData[1] &&
        uuidRegex.test(splitData[1]) &&
        dataSplit[1] === ":projectApiKey" &&
        splitData[2] &&
        splitData[2] === dataSplit[2]
      ) {
        index = i;
      } else if (splitData[1] === dataSplit[1]) {
        index = i;
      }
    }
    return index;
  };
  if (!token && checkURLValidation() < 0) {
    if (userDetails?.project) {
      const projectApiKey = userDetails?.project?.api_key?.split("_")?.[1];
      window.location.href = `${REACT_APP_URL}/${userDetails?.project?.project_identifier}${userDetails?.project?.environment === "SANDBOX" ? "/sandbox" : ""}/${projectApiKey}`;
      dispatch(clearConsumerData());
    } else window.location.href = PUBLIC_NAVIGATION.pageNotFound;
  }
  if (token) {
    if (checkURLValidation() >= 0) {
      let isAuthIndex = -1;
      for (let i = 0; i < publicRouteData.length; i++) {
        const val = publicRouteData[i];
        if (IsInitialisedURL(val)) isAuthIndex = i;
      }
      if (isAuthIndex >= 0) {
        token = null;
        dispatch(clearToken());
        dispatch(setPromptDisabled({ isDisabled: false }));
      } else if (!userDetails?.request_timestamp) {
        window.location.href = PRIVATE_NAVIGATION.profile.edit.path;
      }
    }
  }

  const applySuspense = (element: JSX.Element) => {
    return (
      <Suspense
        fallback={
          <div className="h-screen flex justify-center items-center">
            <Spinner className="!h-12 !w-12" />
          </div>
        }
      >
        {element}
      </Suspense>
    );
  };

  const applyRequiresAuth = (routes: RouteObjType[]): RouteObjType[] => {
    return routes.map((route) => {
      return {
        ...route,
        element: applySuspense(
          <RequiresAuth sidebarId={route.sideBarId} path={route.path}>
            {route.element}
          </RequiresAuth>
        ),
      };
    });
  };

  const applyRequiresUnAuth = (routes: RouteObjType[]): RouteObjType[] => {
    return routes.map((route) => ({
      ...route,
      feature: true,
      permission: true,
      organizationType: true,
      element: applySuspense(
        <RequiresUnAuth
          btnText={route?.btnTitle ?? ""}
          btnNavigatePath={route?.btnNavigatePath ?? ""}
          sidebarId={route.sideBarId ?? 0}
        >
          {route.element}
        </RequiresUnAuth>
      ),
    }));
  };

  const authenticationRoutes = applyRequiresUnAuth(publicRoutes);
  const authenticatedRoutes = applyRequiresAuth(authenticatedRouted);
  const finalRoutes = [...authenticationRoutes, ...authenticatedRoutes];
  const router = createBrowserRouter([...finalRoutes]);
  return <RouterProvider router={router} />;
};

export default Routes;
